import dayjs from 'dayjs';

import type { WorkflowTemplate } from '@/api/workflows';

import { type FieldValue } from './FieldRenderer';
import { qualifiedKey } from './sectionInstances';

/**
 * Substitute `{field:<internalName>}` and `{today:<format>}` tokens in a
 * `StepFileEntry.filenameTemplate` string against the current answers.
 *
 * `{field:<internalName>}` resolves to the value of the field whose
 * `internalName` matches (case-sensitive, exact match). Missing / blank
 * fields resolve to the empty string — the caller can decide whether to
 * fall back to the label. Multi-value fields (CheckboxGroup, arrays)
 * join with `_`; booleans → `yes` / `no`; dates → the raw ISO value.
 *
 * `{today:<format>}` supports the common patterns BMO PDFs use:
 *   MMDDYYYY / YYYYMMDD / DDMMYYYY / YYYY-MM-DD.
 * Any dayjs format string is accepted; unknown formats fall through to
 * dayjs's own parsing (returns whatever dayjs makes of them).
 *
 * Filesystem-hostile characters (`\/:*?"<>|` and control chars) are
 * sanitized to `_` after substitution so a stray value can't produce a
 * broken filename. A `.pdf` extension is appended when absent so the
 * caller doesn't have to remember. Falls back to `${label}.pdf` when
 * the template is null / empty / resolves to only whitespace.
 */
export function renderFilename(
  template: string | null | undefined,
  fallbackLabel: string,
  values: Record<string, FieldValue>,
  workflowTemplate: WorkflowTemplate,
  /** Extension to append when the resolved name doesn't already carry
   *  one. Defaults to `pdf` — the PDF-generation path — but callers
   *  packaging advisor-uploaded assets can pass `jpg`, `png`, `zip`,
   *  etc. so the original file type is preserved. Leading dots are
   *  tolerated (`.pdf` and `pdf` behave the same). */
  extension: string = 'pdf',
): string {
  const ext = extension.replace(/^\./, '').trim() || 'pdf';
  const fallback = ensureExt(sanitize(fallbackLabel) || 'document', ext);
  if (!template || !template.trim()) return fallback;

  // Build the internalName → candidate-value-keys lookup. A workflow
  // can legitimately carry the SAME internalName in multiple sections —
  // branch A ("Beneficiary IS Account Holder") and branch B ("Beneficiary
  // is NOT AH, Individual") both mint an AH1 Last Name field, both
  // stamped `lastname1`. Only ONE of those sections is visible at a
  // time, so on lookup we walk all candidates and return the first key
  // that actually holds a value. Repeatable sections contribute
  // `inst:<sectionId>:0:<fieldId>`; non-repeatable contribute the bare
  // field id. Layout blocks and unnamed fields are skipped.
  const valueKeysByInternalName = new Map<string, string[]>();
  for (const step of workflowTemplate.steps) {
    if (step.type !== 'DynamicForm') continue;
    for (const section of step.sections) {
      const isRepeatable = section.repeatable === true;
      for (const row of section.rows) {
        for (const col of row.columns) {
          const f = col.field;
          if (!f) continue;
          const key = isRepeatable ? qualifiedKey(section.id, 0, f.id) : f.id;
          // A field can now map to several AcroForm names (across PDFs); the
          // `{field:<name>}` token matches on any of them. Dedupe names within
          // one field so a field mapped to the same name twice doesn't push a
          // duplicate value key.
          const names = new Set<string>();
          for (const m of f.pdfMappings ?? []) {
            const name = m.acroFieldName?.trim();
            if (name) names.add(name);
          }
          for (const name of names) {
            const bucket = valueKeysByInternalName.get(name);
            if (bucket) bucket.push(key);
            else valueKeysByInternalName.set(name, [key]);
          }
        }
      }
    }
  }
  const resolveFieldValue = (internalName: string): string => {
    const candidates = valueKeysByInternalName.get(internalName);
    if (!candidates) return '';
    for (const key of candidates) {
      const raw = values[key];
      const formatted = formatFieldValue(raw);
      if (formatted.length > 0) return formatted;
    }
    return '';
  };

  const substituted = template.replace(/\{([a-zA-Z]+):([^}]*)\}/g, (_match, kind, arg) => {
    const argument = String(arg);
    if (kind === 'field') return resolveFieldValue(argument);
    if (kind === 'today') return dayjs().format(argument);
    // Unknown token — leave the literal in place so a typo is visible
    // rather than silently producing an empty slot.
    return `{${kind}:${argument}}`;
  });

  const cleaned = sanitize(substituted).trim();
  if (!cleaned) return fallback;
  return ensureExt(cleaned, ext);
}

/** Coerce any FieldValue to a filename-friendly string chunk. */
function formatFieldValue(v: FieldValue): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number') return Number.isFinite(v) ? String(v) : '';
  if (typeof v === 'boolean') return v ? 'yes' : 'no';
  if (Array.isArray(v)) {
    return v
      .map((item) => (typeof item === 'string' || typeof item === 'number' ? String(item) : ''))
      .filter(Boolean)
      .join('_');
  }
  return '';
}

/** Strip characters that break filenames on Windows / macOS / Linux. */
function sanitize(s: string): string {
  return (
    s
      .replace(/[\\/:*?"<>|]/g, '_')
      // Collapse control chars (\x00-\x1F and DEL) that would either
      // display as odd glyphs or get outright rejected by macOS Finder.
      // eslint-disable-next-line no-control-regex
      .replace(/[\x00-\x1F\x7F]/g, '_')
  );
}

/** Append the given extension when the name doesn't already end in it
 *  (case-insensitive). Empty / `pdf`-equivalent extensions all work. */
function ensureExt(name: string, extension: string): string {
  const re = new RegExp(`\\.${escapeRegExp(extension)}$`, 'i');
  return re.test(name) ? name : `${name}.${extension}`;
}

/** Escape user-supplied text for use inside a RegExp — the extension is
 *  originator-authored and could contain a `.`. */
function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
